import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AIAnalysisPanel from "@/pages/psy/AIAnalysisPanel";

const RISK_BAR = (level) => {
  const map = {
    1: { bg: "bg-emerald-500",   text: "text-emerald-900",   light: "bg-emerald-50" },
    2: { bg: "bg-amber-500",     text: "text-amber-900",     light: "bg-amber-50" },
    3: { bg: "bg-orange-500",    text: "text-orange-900",    light: "bg-orange-50" },
    4: { bg: "bg-rose-600",      text: "text-rose-900",      light: "bg-rose-50" },
    0: { bg: "bg-slate-300",     text: "text-slate-600",     light: "bg-slate-50" },
  };
  return map[level] || map[0];
};

export default function CampaignDetail() {
  const { cid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/psy/campaigns/${cid}/results`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || "Erro ao carregar"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [cid]);

  const onDelete = async () => {
    if (!window.confirm("Excluir esta campanha e todas as respostas? Esta ação é irreversível.")) return;
    try {
      await api.delete(`/psy/campaigns/${cid}`);
      toast.success("Campanha excluída.");
      navigate("/app/testes-psicossociais");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao excluir");
    }
  };

  const copyPublicLink = () => {
    const url = `${window.location.origin}/teste/${data?.campaign?.public_token}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Link copiado!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="text-slate-400 p-8">Carregando resultados…</div>;
  if (!data) return null;

  const c = data.campaign;
  const sc = data.scoring;
  const publicUrl = `${window.location.origin}/teste/${c.public_token}`;
  const overallCls = RISK_BAR(sc.overall.level);

  return (
    <div className="space-y-6" data-testid="psy-campaign-detail">
      {/* Header */}
      <header className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <Link to="/app/testes-psicossociais" className="text-sm text-slate-500 hover:text-slate-800 mb-3 inline-block">
          <i className="fa-solid fa-arrow-left mr-1"></i> Voltar para Testes Psicossociais
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-bold text-slate-900" data-testid="campaign-title">{c.titulo}</h1>
            <p className="text-sm text-slate-500 mt-1">
              <i className="fa-solid fa-tag mr-1"></i> {c.test_name}
              {c.setor_ghe && <> · <i className="fa-solid fa-sitemap"></i> {c.setor_ghe}</>}
              {c.funcao && <> · <i className="fa-solid fa-briefcase"></i> {c.funcao}</>}
            </p>
            <p className="text-sm text-slate-500 mt-0.5">
              <i className="fa-solid fa-building mr-1"></i> {c.company_snapshot?.nome_fantasia || c.company_snapshot?.razao_social}
            </p>
          </div>
          <button
            onClick={onDelete}
            className="text-sm text-rose-700 hover:text-rose-900 border border-rose-200 rounded-lg px-3 py-1.5 hover:bg-rose-50"
            data-testid="btn-delete-campaign"
          >
            <i className="fa-solid fa-trash mr-1"></i> Excluir
          </button>
        </div>
      </header>

      {/* Public link */}
      <section className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 p-6">
        <div className="flex items-center gap-3 mb-3">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white">
            <i className="fa-solid fa-link"></i>
          </span>
          <div>
            <h2 className="font-bold text-slate-900">Link público de aplicação</h2>
            <p className="text-xs text-slate-600">Compartilhe com os colaboradores via WhatsApp, e-mail ou QR Code. Não exige login.</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            readOnly
            value={publicUrl}
            className="flex-1 min-w-[300px] rounded-lg border border-emerald-200 bg-white px-3 py-2.5 text-sm font-mono text-slate-700"
            data-testid="campaign-public-url"
          />
          <button
            onClick={copyPublicLink}
            className="rounded-lg bg-emerald-600 text-white font-semibold px-4 py-2.5 hover:bg-emerald-700"
            data-testid="btn-copy-public-link"
          >
            <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"} mr-1`}></i>
            {copied ? "Copiado!" : "Copiar"}
          </button>
          <a
            href={publicUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-lg border border-emerald-300 bg-white text-emerald-700 font-semibold px-4 py-2.5 hover:bg-emerald-50"
          >
            <i className="fa-solid fa-arrow-up-right-from-square mr-1"></i>
            Abrir
          </a>
        </div>
      </section>

      {/* KPIs */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Respostas</div>
          <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="kpi-responses">{sc.response_count}</div>
          {data.participacao_pct !== null && (
            <div className="text-xs text-slate-500 mt-1">{data.participacao_pct}% de participação</div>
          )}
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Score geral</div>
          <div className="text-3xl font-bold text-slate-900 mt-1">{sc.overall_score.toFixed(2)}</div>
          <div className={`text-xs font-bold mt-1 inline-block px-2 py-0.5 rounded ${overallCls.light} ${overallCls.text}`}>
            {sc.overall.label}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Críticos</div>
          <div className="text-3xl font-bold text-rose-700 mt-1">{sc.critical_count}</div>
          <div className="text-xs text-slate-500 mt-1">marcações de risco máximo</div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Plano de ação</div>
          <div className="text-3xl font-bold text-slate-900 mt-1">{data.plano_acao.length}</div>
          <div className="text-xs text-slate-500 mt-1">ações priorizadas geradas</div>
        </div>
      </section>

      {/* Dimensions chart */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="font-bold text-slate-900 mb-4"><i className="fa-solid fa-chart-column mr-2 text-slate-400"></i>Score por dimensão</h2>
        <div className="space-y-3">
          {sc.dimensions.map((d) => {
            const cls = RISK_BAR(d.level);
            const widthPct = (d.score / 5) * 100;
            return (
              <div key={d.id} data-testid={`dim-row-${d.id}`}>
                <div className="flex justify-between items-baseline mb-1">
                  <div className="text-sm font-semibold text-slate-800">{d.name}</div>
                  <div className="text-xs flex items-baseline gap-2">
                    <span className="font-mono font-bold text-slate-900">{d.score.toFixed(2)}</span>
                    <span className={`font-bold px-2 py-0.5 rounded ${cls.light} ${cls.text}`}>{d.label}</span>
                  </div>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${cls.bg} rounded-full transition-all duration-500`} style={{ width: `${widthPct}%` }}></div>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{d.description}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Risk Matrix */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <header className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-900"><i className="fa-solid fa-grid-2 mr-2 text-slate-400"></i>Matriz de risco psicossocial</h2>
        </header>
        {data.matriz_risco.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">Sem dados suficientes ainda. Aguarde respostas.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="psy-risk-matrix">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left">Fator psicossocial</th>
                  <th className="px-4 py-3 text-center">Média</th>
                  <th className="px-4 py-3 text-center">Severidade</th>
                  <th className="px-4 py-3 text-center">Probab.</th>
                  <th className="px-4 py-3 text-left">Nível</th>
                  <th className="px-4 py-3 text-left">Prioridade</th>
                  <th className="px-4 py-3 text-center">Respostas</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.matriz_risco.map((m, idx) => (
                  <tr key={idx}>
                    <td className="px-4 py-3 font-semibold text-slate-800">{m.fator}<div className="text-xs font-normal text-slate-500">{m.descricao}</div></td>
                    <td className="px-4 py-3 text-center font-mono font-bold">{m.media.toFixed(2)}</td>
                    <td className="px-4 py-3 text-center">{m.severidade}</td>
                    <td className="px-4 py-3 text-center">{m.probabilidade}</td>
                    <td className="px-4 py-3">
                      <span className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: m.color }}>{m.nivel}</span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-700">{m.prioridade}</td>
                    <td className="px-4 py-3 text-center text-slate-500">{m.responses}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Action plan */}
      <section className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <header className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
          <h2 className="font-bold text-slate-900"><i className="fa-solid fa-list-check mr-2 text-slate-400"></i>Plano de ação automático</h2>
          <span className="text-xs text-slate-500">Gerado a partir das dimensões com risco moderado, alto ou crítico.</span>
        </header>
        {data.plano_acao.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm">
            <i className="fa-solid fa-check-double text-3xl block mb-3 text-emerald-500"></i>
            Nenhuma dimensão atingiu o nível moderado ou superior. Mantenha o monitoramento.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="psy-action-plan">
            {data.plano_acao.map((p) => (
              <li key={p.id} className="p-5">
                <div className="flex flex-wrap items-start gap-3 mb-2">
                  <span className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: p.color }}>
                    {p.nivel}
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{p.tipo}</span>
                  <span className="text-xs font-semibold text-slate-700">Prioridade: {p.prioridade}</span>
                  <h3 className="font-bold text-slate-900 w-full">{p.fator} <span className="text-slate-400 font-normal">— média {p.media.toFixed(2)}</span></h3>
                </div>
                <p className="text-sm text-slate-700 mt-1"><b>Problema:</b> {p.problema}</p>
                <p className="text-sm text-slate-700 mt-1"><b>Evidência:</b> {p.evidencia}</p>
                <p className="text-sm text-slate-700 mt-1"><b>Causa provável:</b> {p.causa}</p>
                <p className="text-sm text-slate-700 mt-1"><b>Ação recomendada:</b> {p.acao}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                  <div><span className="text-slate-500 block">Responsável</span><span className="font-medium text-slate-800">{p.responsavel}</span></div>
                  <div><span className="text-slate-500 block">Prazo</span><span className="font-medium text-slate-800">{p.prazo}</span></div>
                  <div><span className="text-slate-500 block">Indicador</span><span className="font-medium text-slate-800">{p.indicador}</span></div>
                  <div><span className="text-slate-500 block">Verificação</span><span className="font-medium text-slate-800">{p.verificacao}</span></div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* AI Interpretive Analysis (Phase 2) */}
      <AIAnalysisPanel campaignId={cid} responseCount={sc.response_count} />

      <footer className="text-xs text-slate-400 text-center py-4">
        <i className="fa-solid fa-shield-halved mr-1"></i>
        Os resultados desta avaliação não substituem avaliação clínica individual nem diagnóstico médico.
      </footer>
    </div>
  );
}
